/*
 * be-snap — Safari/WebKit run-origin snapping for the Swis721 BT Coverage faces.
 *
 * Coverage puts every rectangle edge on the pixel grid relative to the glyph
 * origin; layout then parks the run at a fractional device x/y and WebKit
 * re-AAs every edge. This measures each target element's own layout position
 * and applies a compensating sub-pixel translate so the origin lands on an
 * integer device pixel at the live DPR (the Notes/41 §4 BitmapTextAlign fix,
 * DOM edition; == the lab's `t-snap` column).
 *
 * Runs on EVERY engine (changed 2026-07-20). It used to be WebKit-only on the
 * grounds that "Gecko must never be transformed" and that Chromium was covered
 * by will-change. Both were measured wrong at fractional DPR (Notes/49 §2b):
 *
 *   - Gecko-Windows at DPR 1.5 holds only 4/16 sub-pixel phases without a snap
 *     and 16/16 with one. The old rule was derived at DPR 1.0, where a snap is
 *     merely unnecessary, and does not generalise.
 *   - Chromium's will-change is 16/16 at integer DPRs but 12/16 at 1.5, because
 *     promotion rounds a layer's origin to an integer CSS pixel — which is only
 *     an integer DEVICE pixel when the DPR is an integer.
 *
 * An unpromoted snap is 16/16 on every engine at every DPR measured, and is
 * harmless where it was not needed. So it applies everywhere.
 *
 * CRITICAL: it must NOT be combined with promotion. A promoted layer composites
 * at its rounded origin and DISCARDS the correction inside it, measuring 3-4/16
 * — worse than doing nothing. snap() therefore clears will-change on every
 * element it touches, undoing the pixel-perfect.css floor exactly where this
 * takes over.
 *
 * Framework-free: call beSnapInit() once (returns a disposer). Targets
 * [data-be-snap] and .be-pixel-text elements.
 */

const SEL = "[data-be-snap], .be-pixel-text";

/**
 * Is this an Apple platform? If so, the coverage faces need NO treatment.
 *
 * MEASURED 2026-07-21 (lab sweep, 6 captures across Chrome / Safari / Firefox on
 * macOS at DPR 1 and 2): the UNTREATED face renders the exact BeOS ramp
 * 0/36/73/109/146/182/219 in 5 of 6 — the sixth being a colour-managed capture,
 * not a rendering result. macOS composites text in grayscale, so there is no
 * subpixel fringe for the blend to fix, and nothing for the snap to correct.
 *
 * The treatments are not merely idle there, they are HARMFUL: a snap transform
 * goes stale on Safari zoom (which changes neither devicePixelRatio nor window
 * size, so no re-run fires) and the run stays broken even after zooming back.
 *
 * WHY THIS IS JS AND NOT CSS: CSS has no OS query. @supports can identify an
 * ENGINE — (-moz-appearance: none) for Gecko, (-webkit-hyphens: none) for
 * Safari — but every such test cuts across the axis we need. Firefox-on-macOS
 * and Firefox-on-Windows are CSS-indistinguishable, and they are precisely the
 * case we must separate: Windows needs the full recipe, macOS needs none of it.
 * There is no feature detection for "this compositor does not subpixel-filter
 * text", which is the real condition. So: sniff once, publish it as an
 * attribute, and let CSS key off that.
 *
 * navigator.platform is deprecated but universally implemented; userAgentData
 * covers Blink. iPadOS reports "MacIntel", which is harmless here — both are
 * Apple and both are excluded.
 */
function isApplePlatform(): boolean {
	if (typeof navigator === "undefined") return false;
	// biome-ignore lint/suspicious/noExplicitAny: userAgentData is not in lib.dom yet
	const uaP = (navigator as any).userAgentData?.platform as string | undefined;
	if (uaP) return /mac|ios|iphone|ipad/i.test(uaP);
	return /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");
}

/**
 * Is this Windows? Needed because the Gecko blend is a WINDOWS lever, not a
 * Gecko one: it exists to suppress the ClearType subpixel fringe DirectWrite
 * lays over the COLR face (Notes/47, #400). Gecko on Linux and Android has no
 * ClearType, so the blend buys nothing there — and it is not free, since
 * mix-blend-mode forces the surrounding stacking context to composite as a
 * group and washes out neighbouring untreated text.
 *
 * Until 2026-07-29 the platform was published as apple/other, so every non-Apple
 * Gecko got the blend regardless of OS.
 */
function isWindowsPlatform(): boolean {
	if (typeof navigator === "undefined") return false;
	// biome-ignore lint/suspicious/noExplicitAny: userAgentData is not in lib.dom yet
	const uaP = (navigator as any).userAgentData?.platform as string | undefined;
	if (uaP) return /win/i.test(uaP);
	return /Win/.test(navigator.platform || "");
}

/**
 * Publish the platform as `data-be-platform` on <html> so pixel-perfect.css can
 * gate on it. Safe to call more than once.
 *
 * Call this as EARLY as possible — ideally from an inline script in <head>,
 * before the stylesheet applies — or Apple users get one frame of blended text.
 */
export function bePlatformInit(): void {
	if (typeof document === "undefined") return;
	// three values, not two: "windows" is what the Gecko fringe blend keys off.
	// KEEP IN SYNC with the inline pre-paint duplicate in every *.html <head>.
	document.documentElement.dataset.bePlatform = isApplePlatform() ? "apple" : isWindowsPlatform() ? "windows" : "other";
}

/** Extra selector merged into SEL, for coverage runs a consumer cannot mark up
 * individually (a page's own headings, say, spread across many files). Set via
 * the `also` option so callers do not have to touch every element. */
let extraSel = "";

/**
 * Round to the device grid, breaking EXACT .5 ties toward the smaller
 * coordinate (i.e. up/left on screen) rather than away from zero.
 *
 * Not a nicety — ties here are systematic, not random. A control centres its
 * label by halving the leftover space, so an odd leftover puts every label on a
 * clean .5 boundary: measured at DPR 1.0, all 8 buttons on the demo page sat at
 * exactly X.5 device px. Math.round is round-half-UP, so it pushed every one of
 * them half a pixel DOWN, in the same direction, which reads as the labels
 * sitting a pixel low. (At DPR 1.5 they land on .25, there is no tie, and the
 * correction is fine either way — which is why this only showed at 1.0.)
 *
 * Toward-smaller is also what BeOS did: its centring is integer division,
 * (height - textHeight) / 2, which truncates.
 */
function gridRound(v: number): number {
	return Math.ceil(v - 0.5);
}

/**
 * Is a static correction even MEANINGFUL at this DPR?
 *
 * The correction is a fixed translate, but the thing it corrects — the run's
 * position on the device grid — moves with scroll. At an integer DPR one CSS
 * pixel of scroll is a whole number of device pixels, so scrolling preserves the
 * sub-pixel phase and one correction holds everywhere. At DPR 1.5 it does not:
 * measured on the specimen page, a snapped run alternates offGrid 0.0 / 0.5 with
 * every single CSS pixel of scroll, while the UNTREATED run sits at a constant
 * 0.25. So half the time the snap leaves the run maximally off-grid — worse than
 * never having touched it, because untreated text has no correction to go stale.
 *
 * There is no fix for that short of re-snapping every frame, which is the mobile
 * wobble. So at a fractional DPR we do not snap at all, which is also the
 * position Notes/50 and Notes/51 already take: fractional DPR is accepted soft.
 */
function snapHoldsAtDpr(dpr: number): boolean {
	return Math.abs(dpr - Math.round(dpr)) < 1e-6;
}

/**
 * Translate `el` so its layout origin lands on the device-pixel grid.
 *
 * Measured in DOCUMENT space, not viewport space. getBoundingClientRect() is
 * viewport-relative, so the same element yields a different correction at every
 * scroll offset — and any re-run mid-scroll (a mobile toolbar animating fires
 * visualViewport.resize continuously) writes a different transform each time,
 * which is the y-axis wobble. Adding the scroll offset back makes the value
 * SCROLL-INVARIANT: every re-run during a scroll now computes the identical
 * correction and nothing moves. At an integer DPR — the only case we snap in,
 * see snapHoldsAtDpr — the two forms are equivalent, so this costs no accuracy.
 */
export function gridCorrection(viewportPos: number, scrollPos: number, dpr: number): number {
	// Document space = viewport position + scroll offset. This is what makes the
	// correction SCROLL-INVARIANT: the same element yields the same number at
	// every scroll offset, so a re-run mid-scroll (a mobile toolbar firing
	// visualViewport.resize continuously) writes the identical transform and
	// nothing moves. Measuring viewport-relative instead is the y-axis wobble.
	const doc = viewportPos + scrollPos;
	return (gridRound(doc * dpr) - doc * dpr) / dpr;
}

function snap(el: HTMLElement, dpr: number): void {
	// transforming needs a block-level box; only force it on inline runs.
	if (getComputedStyle(el).display === "inline") el.style.display = "inline-block";
	// Undo the pixel-perfect.css promotion. Promotion + a sub-pixel correction
	// measured WORSE than baseline (3-4/16 vs 4/16), because the layer
	// composites at its rounded origin and throws this transform away.
	el.style.willChange = "auto";
	el.style.transform = "none";
	const r = el.getBoundingClientRect();
	const x = gridCorrection(r.left, window.scrollX, dpr);
	const y = gridCorrection(r.top, window.scrollY, dpr);
	el.style.transform = `translate(${x}px, ${y}px)`;
}

/**
 * Decline to snap, but do NOT hand the element back to the stylesheet.
 *
 * The CSS floor is `will-change: transform`, which is a Chromium lever that is
 * correct only at integer DPRs — it rounds a layer's origin to an integer CSS
 * pixel, which is an integer DEVICE pixel only when the DPR is one. So at the
 * exact DPRs where we refuse to snap, the floor is wrong too, and leaving it in
 * force gives promotion WITHOUT a correction: measured on the specimen page at
 * DPR 1.5, that doubled the level count (20-21 -> 44-45) and pushed the
 * enclosing bevel from 34-39 to 34-56. Worse than either lever alone.
 *
 * Distinct from unsnap(), which really does restore the stylesheet — that is for
 * the disposer, where the caller has switched the treatment off wholesale.
 */
function desnap(el: HTMLElement): void {
	el.style.willChange = "auto";
	el.style.removeProperty("transform");
}

/** Undo `snap`: drop the inline props it set so the stylesheet governs again. */
function unsnap(el: HTMLElement): void {
	// removeProperty, not = "", so `display` falls back to the sheet's value
	// rather than to the empty string; snap only ever forces it on inline runs.
	el.style.removeProperty("transform");
	el.style.removeProperty("will-change");
	el.style.removeProperty("display");
}

/**
 * Snap every coverage run under `root`, and re-snap on the three things that
 * move the run origin: viewport resize, DPR change, and webfont load.
 *
 * Runs on EVERY engine — see the header note. ("No-ops off WebKit" stood here
 * until 2026-07-21 and was wrong from the 2026-07-20 change onward: there is no
 * engine gate in the body, only an SSR guard. It mattered because the snap is
 * half of the shipping recipe on Gecko, and a reader trusting that line would
 * have concluded Firefox got the blend alone — which measures a 90% loss of the
 * opaque glyph core, i.e. the exact defect the snap exists to prevent.)
 *
 * Returns a disposer that removes the listeners.
 */
export function beSnapInit(root: ParentNode = document, opts: { also?: string } = {}): () => void {
	if (typeof window === "undefined") return () => {};

	bePlatformInit();
	/*
	 * THE SNAP GATE IS ENGINE x PLATFORM, NOT PLATFORM ALONE.
	 *
	 * Measured 2026-07-21 on macOS at DPR 2, four faces at their native em,
	 * t-none vs t-gridsnap, counting cells that hold the authentic 7-level ramp:
	 *
	 *     Safari-OSX   t-none 1/4    t-gridsnap 4/4    snap REQUIRED
	 *     Chrome-OSX   t-none 4/4    t-gridsnap 4/4    snap harmless
	 *     FF-OSX       t-none 4/4    t-gridsnap 0/4    snap HARMFUL
	 *
	 * Three engines, one platform, three answers. Safari parks runs at arbitrary
	 * sub-pixel origins and needs the correction (its one clean cell had merely
	 * landed on-grid by luck). Gecko on macOS is already exact and the transform
	 * pushes it OFF — 175/148/58/50 levels where baseline held 7.
	 *
	 * Gecko is the case that flips on PLATFORM: it needs the snap on Windows
	 * (blend without snap costs 90% of the opaque core) and must not have it on
	 * macOS. Same engine, opposite answers — which is precisely why no @supports
	 * rule can express this and why the platform is sniffed.
	 *
	 * An earlier version of this gate skipped on ALL Apple platforms. That was
	 * wrong for Safari: it removed the one lever WebKit actually needs.
	 *
	 * WIDENED 2026-07-29 from "Gecko on Apple" to "Gecko anywhere but Windows",
	 * measured by tests/pixel-probe/probe.ts on headless Firefox/Linux — 8
	 * sub-pixel phases, DPR 1/1.5/2, reading PAINTED PIXELS rather than boxes:
	 *
	 *     DPR   untreated levels   with snap
	 *     1.0        7 (drift 0)     7-55
	 *     1.5       22-27            22-76
	 *     2.0        7 (drift 0)     7-71
	 *
	 * Untreated Gecko-Linux already paints the authentic 7-level ramp with ZERO
	 * drift across every phase. The snap's fractional translate forces the run to
	 * rasterise off the grid it was already on, exploding the level count. It also
	 * damaged the enclosing BUTTON BEVEL (10-14 levels -> 11-58) — a box with no
	 * text in it — which is the measured form of "treating some runs breaks other
	 * parts of the page".
	 *
	 * So the rule is not "Gecko flips on Apple", it is: Gecko wants the snap ONLY
	 * where the blend forces it to, and the blend is Windows-only.
	 */
	const isGecko = typeof CSS !== "undefined" && CSS.supports?.("-moz-appearance", "none");
	if (isGecko && !isWindowsPlatform()) return () => {};

	extraSel = opts.also?.trim() ?? "";
	const sel = extraSel ? `${SEL}, ${extraSel}` : SEL;

	const run = () => {
		const dpr = window.devicePixelRatio || 1;
		// Re-checked per run, not once at init: a window dragged to another monitor
		// changes DPR, and a correction that was valid at 2.0 is actively harmful
		// at 1.5. desnap (not unsnap) because the CSS promotion floor is wrong at
		// exactly the DPRs where the snap is — see desnap's note.
		const ok = snapHoldsAtDpr(dpr);
		for (const el of root.querySelectorAll<HTMLElement>(sel)) {
			if (ok) snap(el, dpr);
			else desnap(el);
		}
	};
	const onChange = () => requestAnimationFrame(run);

	window.addEventListener("resize", onChange);

	/*
	 * ZOOM. This is the staleness bug, and it is worse than simply not helping:
	 * a correction computed for the OLD layout stays applied, so the run is
	 * pushed OFF the grid it was on. Zoom out again and it stays broken, because
	 * nothing recomputes. Untreated text is immune — it has no transform to go
	 * stale — which is exactly the reported "baseline is perfect, the treated
	 * text breaks and never recovers".
	 *
	 * The three listeners above cannot see it on WebKit: Safari page zoom does
	 * NOT change devicePixelRatio and does NOT resize the window, so neither the
	 * resize handler nor the DPR media query fires.
	 *
	 * visualViewport.resize DOES fire for page and pinch zoom, and the
	 * ResizeObserver catches any other reflow that moves runs (a font swap
	 * changing metrics, a container animating, content loading in). run()
	 * recomputes from a cleared transform every time, so extra firings are
	 * harmless — the failure mode is missing one, never running twice.
	 */
	window.visualViewport?.addEventListener("resize", onChange);
	let ro: ResizeObserver | null = null;
	if (typeof ResizeObserver !== "undefined") {
		ro = new ResizeObserver(onChange);
		ro.observe(document.documentElement);
	}

	/*
	 * CONTENT CHANGES. The listeners above all key off GEOMETRY, so they miss the
	 * two cases that matter most in a live app:
	 *
	 *   - A run that did not exist at init is never snapped at all. Anything
	 *     mounted later -- a React commit, a tab switching panels, a menu opening
	 *     -- renders untreated no matter how many times the window resizes.
	 *   - Existing runs MOVE when content is inserted above them, which leaves
	 *     their correction stale: computed for the old position, still applied.
	 *     A stale correction is worse than none, because it pushes a run that
	 *     may now be perfectly on-grid back off it.
	 *
	 * So re-run on DOM mutations too. run() always recomputes from a cleared
	 * transform, so a redundant firing costs a measure and changes nothing --
	 * the failure mode is missing one, never running twice.
	 *
	 * Only childList and characterData are observed. snap() writes `transform`
	 * and `display`, so observing ATTRIBUTES here would make it retrigger itself
	 * forever.
	 */
	let mo: MutationObserver | null = null;
	if (typeof MutationObserver !== "undefined") {
		mo = new MutationObserver(onChange);
		mo.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
	}

	// a fresh media query per current DPR fires `change` when the DPR shifts
	// (monitor move / zoom); re-arm it each time so it tracks the new value.
	let mq: MediaQueryList | null = null;
	const armDpr = () => {
		mq?.removeEventListener?.("change", onDpr);
		mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
		mq.addEventListener?.("change", onDpr);
	};
	const onDpr = () => {
		armDpr();
		onChange();
	};
	armDpr();

	document.fonts?.ready?.then(run);
	run();

	return () => {
		window.removeEventListener("resize", onChange);
		window.visualViewport?.removeEventListener("resize", onChange);
		ro?.disconnect();
		mo?.disconnect();
		mq?.removeEventListener?.("change", onDpr);
		// A disposer that only unhooks listeners leaves the LAST snap frozen in
		// the inline style, so turning the effect off would still show its
		// translate. Restore what snap() wrote.
		for (const el of root.querySelectorAll<HTMLElement>(sel)) unsnap(el);
	};
}
